import React, { useState } from 'react';
import {
    Container,
    Card,
    CardBody,
    Button,
    Navbar,
    NavbarBrand
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
    FaArrowLeft,
    FaUserCircle,
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaChevronDown,
    FaChevronRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { authActions } from '../sagas/authSlice';
import logo from '../assets/images/logo.png';
import GlobalJobCenter from '../components/BackgroundJobs/GlobalJobCenter';
import './ModuleLayout.css';

const ModuleLayout = ({ children, moduleConfig, breadcrumbs }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { fullName, businessName } = useSelector(state => state.auth);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState(null);
    const ModuleIcon = moduleConfig?.icon;

    const handleLogout = () => {
        Swal.fire({
            title: t('auth.logoutConfirmTitle'),
            text: t('auth.logoutConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(authActions.logout());
                Swal.fire({
                    icon: 'success',
                    title: t('auth.logoutSuccess'),
                    showConfirmButton: false,
                    timer: 1500
                }).then(() => {
                    navigate('/login');
                });
            }
        });
    };

    const handleBackToModules = () => {
        localStorage.removeItem('selectedModule');
        navigate('/modules');
        setMobileSidebarOpen(false);
    };

    const handleMenuToggle = () => {
        if (window.innerWidth <= 768) {
            setSidebarCollapsed(false);
            setMobileSidebarOpen(prev => !prev);
            return;
        }

        setSidebarCollapsed(prev => !prev);
    };

    const handleNavigate = (path) => {
        navigate(path);
        setMobileSidebarOpen(false);
    };

    const renderMenuItem = (item, isSubItem = false) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        return (
            <Card
                key={item.path || item.label}
                className={`sidebar-menu-card sidebar-menu-item ${isSubItem ? 'subitem' : ''} ${isActive ? 'active' : ''}`}
                onClick={() => handleNavigate(item.path)}
            >
                <CardBody className="sidebar-menu-body">
                    <Icon size={isSubItem ? 14 : 16} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                </CardBody>
            </Card>
        );
    };

    return (
        <div
            className="module-layout-wrapper"
            style={{ '--module-accent': moduleConfig?.color || '#2563eb' }}
        >
            <div className="module-header-wrapper">
                <Navbar className="module-navbar compact-navbar" expand="lg">
                    <Container fluid className="module-navbar-inner">
                        <div className="module-top-row">
                            <div className="module-top-left">
                                <Button
                                    color="link"
                                    className="sidebar-toggle-btn"
                                    onClick={handleMenuToggle}
                                    aria-label="Basculer la navigation"
                                >
                                    {sidebarCollapsed ? <FaBars size={16} /> : <FaTimes size={16} />}
                                </Button>

                                <NavbarBrand className="module-brand">
                                    <img
                                        src={logo}
                                        alt="MatjarPos"
                                        height="42"
                                        className="module-brand-logo"
                                    />
                                    <div className="brand-info">
                                        <div className="business-name">{businessName}</div>
                                        <div className="module-indicator">
                                            {ModuleIcon ? <ModuleIcon size={13} /> : null}
                                            <span className="module-name">{moduleConfig?.title}</span>
                                        </div>
                                    </div>
                                </NavbarBrand>
                            </div>

                            <div className="module-top-center">
                                <span className="module-top-kicker">Espace de travail</span>
                                <strong>{moduleConfig?.title}</strong>
                            </div>

                            <div className="module-top-actions">
                                <GlobalJobCenter />

                                <div className="module-user-pill">
                                    <FaUserCircle size={18} />
                                    <span>{fullName}</span>
                                </div>

                                <Button
                                    className="logout-btn"
                                    onClick={handleLogout}
                                    aria-label={t('nav.signOut')}
                                >
                                    <FaSignOutAlt size={13} />
                                </Button>
                            </div>
                        </div>

                        <div className="module-breadcrumb-row">
                            <nav className="breadcrumb-nav" aria-label="Fil d'Ariane">
                                <span className="breadcrumb-item" onClick={handleBackToModules}>
                                    Modules
                                </span>
                                <span className="breadcrumb-separator">/</span>
                                <span className="breadcrumb-item" onClick={() => handleNavigate('')}>
                                    {moduleConfig?.title}
                                </span>

                                {breadcrumbs?.map((breadcrumb, index) => (
                                    <React.Fragment key={index}>
                                        <span className="breadcrumb-separator">/</span>
                                        {breadcrumb.href ? (
                                            <span
                                                className="breadcrumb-item"
                                                onClick={() => handleNavigate(breadcrumb.href)}
                                            >
                                                {breadcrumb.label}
                                            </span>
                                        ) : (
                                            <span className="breadcrumb-current">
                                                {breadcrumb.label}
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </nav>

                            <small className="module-date">
                                {new Date().toLocaleDateString('fr-FR', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                })}
                            </small>
                        </div>
                    </Container>
                </Navbar>
            </div>

            <div className="module-content-wrapper">
                {mobileSidebarOpen ? (
                    <button
                        type="button"
                        className="module-sidebar-backdrop"
                        aria-label="Fermer la navigation"
                        onClick={() => setMobileSidebarOpen(false)}
                    />
                ) : null}

                <aside className={`module-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-module-lockup">
                            <span className="sidebar-module-icon">
                                {ModuleIcon ? <ModuleIcon size={22} /> : null}
                            </span>
                            {!sidebarCollapsed && (
                                <div>
                                    <h6>{moduleConfig?.title}</h6>
                                    <small>{moduleConfig?.description}</small>
                                </div>
                            )}
                        </div>

                        {!sidebarCollapsed && (
                            <Button
                                color="link"
                                size="sm"
                                className="sidebar-close-btn"
                                onClick={() => {
                                    setSidebarCollapsed(true);
                                    setMobileSidebarOpen(false);
                                }}
                            >
                                <FaTimes size={14} />
                            </Button>
                        )}
                    </div>

                    <div className="sidebar-menu">
                        <Card className="sidebar-menu-card sidebar-back-card">
                            <CardBody className="sidebar-menu-body" onClick={handleBackToModules}>
                                <FaArrowLeft size={16} />
                                {!sidebarCollapsed && <span>{t('navigation.backToModules')}</span>}
                            </CardBody>
                        </Card>

                        {moduleConfig?.menuItems?.map((item, idx) => (
                            item.subMenu ? (
                                <div key={item.label || idx} className="sidebar-menu-group">
                                    <Card
                                        className={`sidebar-menu-card sidebar-menu-item has-submenu ${openSubMenu === item.label ? 'active' : ''}`}
                                        onClick={() => setOpenSubMenu(openSubMenu === item.label ? null : item.label)}
                                    >
                                        <CardBody className="sidebar-menu-body">
                                            <div className="sidebar-menu-label">
                                                <item.icon size={16} />
                                                {!sidebarCollapsed && <span>{item.label}</span>}
                                            </div>
                                            {!sidebarCollapsed && (
                                                openSubMenu === item.label ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />
                                            )}
                                        </CardBody>
                                    </Card>

                                    {openSubMenu === item.label && (
                                        <div className="sidebar-submenu">
                                            {item.subMenu.map(subItem => renderMenuItem(subItem, true))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                renderMenuItem(item)
                            )
                        ))}
                    </div>
                </aside>

                <main className="module-main-content">
                    <Container fluid className="module-main-container">
                        {children}
                    </Container>
                </main>
            </div>
        </div>
    );
};

export default ModuleLayout;
